'use client';

import { useState } from 'react';
import { BrainCircuitIcon, NeuralParticles } from '@/components/shared/AIComponents';

interface FinkOrbPreviewProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showLabel?: boolean;
  onClick?: () => void;
}

const sizeClasses = {
  sm: { outer: 'w-16 h-16', inner: 'w-14 h-14', icon: 'w-7 h-7' },
  md: { outer: 'w-24 h-24', inner: 'w-20 h-20', icon: 'w-10 h-10' },
  lg: { outer: 'w-32 h-32', inner: 'w-28 h-28', icon: 'w-14 h-14' },
  xl: { outer: 'w-40 h-40', inner: 'w-36 h-36', icon: 'w-16 h-16' },
};

export default function FinkOrbPreview({ size = 'lg', showLabel = true, onClick }: FinkOrbPreviewProps) {
  const [isHovering, setIsHovering] = useState(false);
  const classes = sizeClasses[size];

  return (
    <div className="relative inline-flex flex-col items-center gap-4">
      {/* The Orb */}
      <button
        onClick={onClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className={`relative ${classes.outer} rounded-full flex items-center justify-center transition-all duration-500 cursor-pointer group`}
        aria-label="Fink AI Assistant"
      >
        {/* Outer rotating gradient ring */}
        <div
          className={`
            absolute inset-0 rounded-full
            bg-gradient-to-r from-[#0D60E6] via-cyan-500 to-[#0D60E6]
            ${isHovering ? 'animate-spin-slow opacity-100' : 'opacity-60'}
            blur-sm
          `}
          style={{ animationDuration: '4s' }}
        />

        {/* Pulsing ring effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#0D60E6] to-cyan-500 animate-ping opacity-20" />
        <div className="absolute inset-2 rounded-full bg-gradient-to-r from-[#0D60E6] to-cyan-500 animate-ping opacity-10" style={{ animationDelay: '0.5s' }} />

        {/* Main orb */}
        <div
          className={`
            relative ${classes.inner} rounded-full
            bg-gradient-to-br from-[#0D60E6] via-[#0D52C2] to-cyan-600
            flex items-center justify-center
            shadow-2xl shadow-blue-500/40
            transition-all duration-500
            ${isHovering ? 'scale-105' : 'scale-100'}
          `}
        >
          {/* Neural particles */}
          <NeuralParticles isActive={isHovering} count={8} />

          {/* Inner glow */}
          <div className="absolute inset-4 rounded-full bg-white/10 blur-xl" />

          {/* Icon */}
          <BrainCircuitIcon className={`${classes.icon} text-white relative z-10 drop-shadow-lg`} animate={isHovering} />
        </div>

        {/* Orbiting dots */}
        <div className={`absolute inset-0 ${isHovering ? 'animate-spin-slow' : ''}`} style={{ animationDuration: '8s' }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-[#0D60E6] rounded-full shadow-lg shadow-blue-400/50" />
          <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-blue-400 rounded-full" />
          <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-blue-400 rounded-full" />
        </div>
      </button>

      {/* Label */}
      {showLabel && (
        <div className="text-center">
          <div className="text-lg font-bold bg-gradient-to-r from-[#0D60E6] to-cyan-600 bg-clip-text text-transparent">
            Fink
          </div>
          <div className="text-sm text-gray-500">AI Tax Intelligence</div>
        </div>
      )}
    </div>
  );
}
